import type { Direction, Flow, Region } from "./types";

export const formatTrips = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format;

export function compactTrips(value: number): string {
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: value >= 100_000 ? 1 : 2,
  }).format(value);
}

export function airportFlows(
  flows: Flow[],
  airport: string,
  direction: Direction,
  selected: string[],
): Flow[] {
  const selectedSet = new Set(selected);
  return flows.filter((flow) => {
    const counterpart = direction === "to" ? flow.o : flow.d;
    const matchesAirport = direction === "to" ? flow.d === airport : flow.o === airport;
    return matchesAirport && (selectedSet.size === 0 || selectedSet.has(counterpart));
  });
}

export function counterpartName(flow: Flow, direction: Direction): string {
  return direction === "to" ? flow.o : flow.d;
}

export function regionByName(regions: Region[]): Map<string, Region> {
  return new Map(regions.map((region) => [region.name, region]));
}

export function flowCurve(
  start: [number, number],
  end: [number, number],
  steps = 42,
): Array<[number, number]> {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const distance = Math.hypot(dx, dy);
  if (!distance) return [start, end];
  const bend = Math.min(distance * 0.2, 0.028);
  const control: [number, number] = [
    (start[0] + end[0]) / 2 - (dy / distance) * bend,
    (start[1] + end[1]) / 2 + (dx / distance) * bend,
  ];
  return Array.from({ length: steps }, (_, index) => {
    const t = index / (steps - 1);
    const inverse = 1 - t;
    return [
      inverse * inverse * start[0] + 2 * inverse * t * control[0] + t * t * end[0],
      inverse * inverse * start[1] + 2 * inverse * t * control[1] + t * t * end[1],
    ];
  });
}

export function stableOffset(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}

